import React, { ChangeEvent, DragEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { CloudUploadOutlined } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';

import {
  buttonTextIconLarge,
  gray100,
  gray80,
  red50,
  secondaryButtonBackgroundColor,
} from '../../constants/colors';
import { formLabelWrapper, regularMd, regularSm, regularXs } from '../../constants/location';

type Props = {
  helperText: string | undefined;
  onFilesSelected: (files: FileList | null) => void;
  multiple?: boolean;
  labelText?: string;
  requiredText?: string;
  labelFontSize?: string;
  height?: string;
  fileCountOverText?: string;
  disabled?: boolean;
};

const FileUploader = ({
  helperText,
  onFilesSelected,
  labelText,
  requiredText,
  labelFontSize,
  multiple = true,
  height,
  fileCountOverText,
  disabled = false,
}: Props): ReactElement => {
  const [borderColor, setBorderColor] = useState<string>(gray80);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  if (helperText == undefined && fileCountOverText !== '') {
    helperText = fileCountOverText;
  }
  const styles = {
    button: {
      height: '44px',
      padding: '16px',
      '& .MuiButton-icon': {
        marginRight: '4px',
      },
    },
    errorText: {
      ...regularXs,
      color: red50,
      marginTop: '4px',
    },
    icon24: {
      height: '24px',
      marginLeft: '0px',
      width: '24px',
    },
    input: {
      display: 'none',
    },
    text: {
      ...regularMd,
      color: gray80,
      '@media screen and (min-width: 960px)': {
        ...regularSm,
      },
    },
    uploadArea: {
      alignItems: 'center',
      justifyContent: 'center',
      border: '1px dashed' + gray80,
      borderRadius: '10px',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      padding: '24px 0',
      width: '100%',
      height: height ? height : '129px',
    },
    wrapper: {
      marginBottom: '24px',
      '@media screen and (min-width: 960px)': {
        marginBottom: '16px',
      },
    },
    formLabelWrapper,
    label: {
      fontSize: labelFontSize ? labelFontSize : '14px',
    },
    required: {
      color: red50,
      fontSize: labelFontSize ? labelFontSize : '14px',
      marginLeft: '6px',
    },
  };

  useEffect(() => {
    if (helperText) {
      setBorderColor(red50);
    } else {
      setBorderColor(gray80);
    }
  }, [helperText]);

  // ファイルを選択するボタン押下時
  const handleButtonClick = (): void => {
    if (disabled) return;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // ファイル選択時
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (disabled) return;
    const files = event.target.files;
    onFilesSelected(files);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    if (disabled) return;
    const files = event.dataTransfer.files;
    onFilesSelected(files);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    if (disabled) return;
    setBorderColor(gray100);
  };

  return (
    <Box sx={styles.wrapper}>
      {labelText && (
        <Typography align="left" sx={styles.formLabelWrapper} component="div">
          <Typography color={gray80} sx={styles.label} component="span">
            {labelText}
          </Typography>

          {requiredText && (
            <Typography component="span" sx={styles.required}>
              {requiredText}
            </Typography>
          )}
        </Typography>
      )}
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={(): void => setBorderColor(gray80)}
        sx={[styles.uploadArea, { borderColor: borderColor }]}
      >
        <input
          multiple={multiple}
          onChange={handleFileChange}
          ref={fileInputRef}
          style={styles.input}
          type="file"
          disabled={disabled}
        />
        <Button
          onClick={handleButtonClick}
          startIcon={<CloudUploadOutlined sx={styles.icon24} />}
          sx={[secondaryButtonBackgroundColor, styles.button]}
          disabled={disabled}
        >
          <Typography sx={buttonTextIconLarge}>ファイルを選択する</Typography>
        </Button>
        <Typography sx={styles.text}>またはドラッグ&ドロップ</Typography>
      </Box>
      {helperText && <Typography sx={styles.errorText}>{helperText}</Typography>}
    </Box>
  );
};

export default FileUploader;
